import React from "react";
import { BarChart3, Search, Lock, Globe, FileText, Network, Check } from "lucide-react";

// Severity → Aurora token mapping (logic preserved, points at design tokens)
const SEV_COLORS = {
  critical: "var(--au-error)",
  high: "var(--au-error)",
  medium: "var(--au-warn)",
  low: "var(--au-accent-ink)"
};

export default function RiskBreakdown({ breakdown, verificationId }) {
  if (!breakdown) return null;

  const cats = [
    { key: "ssl",     label: "SSL/TLS",  icon: Lock },
    { key: "dns",     label: "DNS",      icon: Globe },
    { key: "headers", label: "Headers",  icon: FileText },
    { key: "ports",   label: "Ports",    icon: Network }
  ];
  const max = breakdown.max_per_category || { ssl: 20, dns: 15, headers: 25, ports: 40 };

  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      {/* Header with verification badge */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 18,
        paddingBottom: 14,
        borderBottom: "1px solid var(--au-hair)",
        flexWrap: "wrap",
        gap: 12
      }}>
        <div className="au-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={14} aria-hidden="true" /> RISK SCORE BREAKDOWN
        </div>
        {verificationId && (
          <div className="au-chip au-chip-status au-mono" style={{ color: "var(--au-success)" }}>
            <Check size={12} aria-hidden="true" /> {verificationId}
          </div>
        )}
      </div>

      {/* Category bars */}
      <div style={{ display: "grid", gap: 14, marginBottom: 22 }}>
        {cats.map(c => {
          const score = breakdown[c.key] || 0;
          const maxScore = max[c.key] || 25;
          const pct = (score / maxScore) * 100;
          const barColor = pct > 75 ? "var(--au-error)" : pct > 40 ? "var(--au-warn)" : pct > 0 ? "var(--au-accent-ink)" : "var(--au-success)";
          const Icon = c.icon;

          return (
            <div key={c.key}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
                fontSize: 13
              }}>
                <span style={{ color: "var(--au-text-2)", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Icon size={14} aria-hidden="true" />
                  {c.label}
                </span>
                <span className="au-mono" style={{
                  color: barColor,
                  fontWeight: 700
                }}>{score}/{maxScore}</span>
              </div>
              <div style={{
                height: 6,
                background: "var(--au-inset)",
                borderRadius: 3,
                overflow: "hidden"
              }}>
                <div style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: barColor,
                  borderRadius: 3,
                  transition: "width 0.6s ease"
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Findings list with evidence */}
      {breakdown.findings && breakdown.findings.length > 0 && (
        <>
          <div className="au-eyebrow" style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            marginTop: 6
          }}>
            <Search size={14} aria-hidden="true" /> DETAILED FINDINGS WITH EVIDENCE
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {breakdown.findings.map((f, i) => {
              const color = SEV_COLORS[f.severity] || "var(--au-muted)";
              return (
                <div key={i} style={{
                  background: "var(--au-glass)",
                  border: "1px solid var(--au-hair)",
                  borderLeft: `3px solid ${color}`,
                  borderRadius: "var(--au-r-sm)",
                  padding: "12px 16px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                    flexWrap: "wrap",
                    gap: 8
                  }}>
                    <span style={{
                      color: "var(--au-text)",
                      fontSize: 13,
                      fontWeight: 600
                    }}>{f.title}</span>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span className="au-chip au-chip-status" style={{ color, textTransform: "uppercase" }}>
                        <span className="au-led" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
                        {f.severity}
                      </span>
                      <span className="au-chip au-chip-status au-mono" style={{ color: "var(--au-text-2)" }}>
                        {f.confidence}% sure
                      </span>
                    </div>
                  </div>
                  <div className="au-mono" style={{
                    fontSize: 11,
                    color: "var(--au-text-2)",
                    background: "var(--au-inset)",
                    padding: "6px 10px",
                    borderRadius: "var(--au-r-input)",
                    marginTop: 4,
                    wordBreak: "break-word"
                  }}>
                    <span style={{ color: "var(--au-muted)" }}>Evidence:</span> {f.evidence}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
